import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { CheckedListControl } from "@/Common/Components/Inputs/CheckedListControl";
import { MultiDateSelector } from "@/Common/Components/Inputs/MultiDateSelector";
import { BaseSettingsContainer, BaseSettingsContainerProps, BaseSettingsContainerState } from "@/Common/Components/Settings/BaseSettingsContainer";
import { SearchSettingsFooterProps } from "@/Common/Components/Settings/SearchSettingsFooter";
import { SearchMenuHelper } from "@/Common/Helpers/SearchMenuHelper";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { MedicalRequestsInboxSettings } from "@/Portfolio/Models/Settings/MedicalRequestsInboxSettings";
import { IConsultationRequestInboxModel } from "@/Portfolio/Models/UI/IConsultationRequestsInboxModel";
import { ILabRequestsInboxModel } from "@/Portfolio/Models/UI/IRequestsInboxModel";

import "./MedicalRequestSettingsContainer.css";

interface MedicalRequestSettingsContainerProps extends BaseSettingsContainerProps {
    type: MedicalRequestType;
    model: IConsultationRequestInboxModel | ILabRequestsInboxModel;
    onCloseOp: LocalOperations.Portfolio;
    onOkOp: LocalOperations.Portfolio;
    onColumnOrderingButtonClickOp: LocalOperations.Portfolio;
    onResultsPerPageChangeOp: LocalOperations.Portfolio;
}

class MedicalRequestSettingsContainerState extends BaseSettingsContainerState {
    public globalCreatedByList: EmployeeUIModel[];
    public globalRequestingStaffList: EmployeeUIModel[];
    public headerCheckboxes: {
        sites: boolean;
        createdBy: boolean;
        requestingStaff: boolean;
        priority: boolean;
        status: boolean;
    };
    public creationDateFrom: Date;
    public creationDateTo: Date;
    public dueDateFrom: Date;
    public dueDateTo: Date;

    public siteCheckList: SiteUIModel[];

    public createdBySearchText: string;
    public createdByCheckList: EmployeeUIModel[];

    public requestingStaffSearchText: string;
    public requestingStaffCheckList: EmployeeUIModel[];

    public statusCheckList: CheckboxUIModel[];

    public priorityCheckList: CheckboxUIModel[];

    public constructor(init?: Partial<MedicalRequestSettingsContainerState>) {
        super();
        Object.assign(this, init);
    }

    public static default(): MedicalRequestSettingsContainerState {
        return new MedicalRequestSettingsContainerState({
            isSaveSettingsChecked: false,
            globalCreatedByList: [],
            globalRequestingStaffList: [],
            headerCheckboxes: {
                sites: false,
                createdBy: false,
                requestingStaff: false,
                priority: false,
                status: false
            },
            creationDateFrom: null,
            creationDateTo: null,
            dueDateFrom: null,
            dueDateTo: null,
            siteCheckList: [],
            createdBySearchText: "",
            createdByCheckList: [],
            requestingStaffSearchText: "",
            requestingStaffCheckList: [],
            statusCheckList: [],
            priorityCheckList: []
        });
    }
}

class MedicalRequestSettingsContainer extends BaseSettingsContainer<MedicalRequestSettingsContainerProps, MedicalRequestSettingsContainerState> {
    public constructor(props: MedicalRequestSettingsContainerProps) {
        super(props);
        const model = props.model;
        const initialState = MedicalRequestSettingsContainerState.default();

        initialState.headerCheckboxes = {
            sites: model.get("SelectAllSitesChecked"),
            createdBy: model.get("SelectAllCreatedByChecked"),
            requestingStaff: model.get("SelectAllRequestingStaffChecked"),
            priority: model.get("SelectAllPriorityChecked"),
            status: model.get("SelectAllStatusChecked")
        };
        initialState.createdBySearchText = model.get("CreatedByCliniciansSearchText");
        initialState.requestingStaffSearchText = model.get("RequestingStaffsSearchText");
        initialState.creationDateFrom = model.get("CreationDateFrom");
        initialState.creationDateTo = model.get("CreationDateTo");
        initialState.dueDateFrom = model.get("DueDateFrom");
        initialState.dueDateTo = model.get("DueDateTo");
        initialState.siteCheckList = model.get("Sites").toArray();
        initialState.globalCreatedByList = model.get("CreatedByGlobalCache").toArray();
        initialState.globalRequestingStaffList = model.get("RequestingStaffGlobalCache").toArray();
        initialState.priorityCheckList = model.get("PriorityList").toArray();
        initialState.statusCheckList = model.get("StatusList").toArray();

        this.state = initialState;
    }

    public componentDidMount(): void {
        this.updateEmployeeList();
    }

    protected shouldTriggerOkActionOnOutsideClick(): boolean {
        return true;
    }

    protected getHeaders(): { id: string, node: React.ReactNode }[] {
        const headerCheck = this.state.headerCheckboxes;
        return [
            {
                id: "dates",
                node: this.getUpperCaseString("DateLabel")
            },
            {
                id: "allSites",
                node: this.props.model.get("Sites").count() > 1
                    ? (
                        <CheckboxControl
                            label={this.getUpperCaseString("SitesLabel")}
                            onChange={checked => {
                                this.mutateState(
                                    s => {
                                        s.headerCheckboxes.sites = checked;
                                        s.siteCheckList.forEach(x => x.Checked = checked);
                                    },
                                    () => this.updateEmployeeList()
                                );
                            }}
                            checked={headerCheck.sites}
                        />
                    )
                    : null
            },
            {
                id: "allRequestingStaff",
                node: <CheckboxControl
                    label={this.getUpperCaseString("RequestingStaffLabel")}
                    onChange={checked => this.mutateState(s => {
                        s.headerCheckboxes.requestingStaff = checked;
                        s.requestingStaffCheckList.forEach(x => x.Checked = checked);
                    })}
                    checked={headerCheck.requestingStaff}
                />
            },
            {
                id: "allCreatedByClinician",
                node: <CheckboxControl
                    label={this.getUpperCaseString("CreatedByLabel")}
                    checked={headerCheck.createdBy}
                    onChange={checked => this.mutateState(s => {
                        s.headerCheckboxes.createdBy = checked;
                        s.createdByCheckList.forEach(x => x.Checked = checked);
                    })}
                />
            },
            {
                id: "allPriority",
                node: <CheckboxControl
                    label={this.getUpperCaseString("PriorityLabel")}
                    checked={headerCheck.priority}
                    onChange={checked => this.mutateState(s => {
                        s.headerCheckboxes.priority = checked;
                        s.priorityCheckList.forEach(x => x.Checked = checked);
                    })}
                />
            },
            {
                id: "allStatus",
                node: <CheckboxControl
                    label={this.getUpperCaseString("StatusLabel")}
                    checked={headerCheck.status}
                    onChange={checked => this.mutateState(s => {
                        s.headerCheckboxes.status = checked;
                        s.statusCheckList.forEach(x => x.Checked = checked);
                    })}
                />
            }
        ]
            .filter(p => !!p.node);
    }

    protected getColumnData(): { id: string, node: React.ReactNode }[] {
        return [
            {
                id: "dateSelection",
                node: (
                    <div>
                        <MultiDateSelector
                            label={cacheStore.GetString("CreationDateLabel")}
                            id="creationdate-time-span-search"
                            fromChange={date => this.mutateState(s => s.creationDateFrom = date && moment(date).startOf("day").toDate())}
                            toChange={date => this.mutateState(s => s.creationDateTo = date && moment(date).endOf("day").toDate())}
                            fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            from={this.state.creationDateFrom}
                            to={this.state.creationDateTo}
                        />
                        <MultiDateSelector
                            label={cacheStore.GetString("DueDateLabel")}
                            id="dueDate-time-span-search"
                            fromChange={date => this.mutateState(s => s.dueDateFrom = date && moment(date).startOf("day").toDate())}
                            toChange={date => this.mutateState(s => s.dueDateTo = date && moment(date).endOf("day").toDate())}
                            fromSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            toSetTodayLabel={cacheStore.GetString("TodayLabel")}
                            from={this.state.dueDateFrom}
                            to={this.state.dueDateTo}
                        />
                    </div>
                )
            },
            {
                id: "sitesSelection",
                node: this.props.model.get("Sites").count() > 1 && (
                    <div className="sites-column">
                        <div className="medical-settings created-list-control">
                            <CheckedListControl
                                DataSource={this.state.siteCheckList.toImmutable()}
                                onChange={(checked, id) => this.mutateState(
                                    s => s.siteCheckList.firstOrDefault(x => x.Id === id).Checked = checked,
                                    () => this.updateEmployeeList()
                                )}
                                MaxTextLength={25}
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "requestingStaffSelect",
                node: (
                    <div>
                        <input
                            className="form-control medical-settings search-text"
                            value={this.state.requestingStaffSearchText}
                            onChange={(e: any) => {
                                const value = e.target.value;
                                this.mutateState(s => s.requestingStaffSearchText = value, () => this.updateEmployeeList());
                            }}
                            placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                        />
                        <div className="medical-settings site-control">
                            <CheckedListControl
                                idModifier="requestingStaffList"
                                DataSource={this.state.requestingStaffCheckList.toImmutable()}
                                onChange={(checked, id) => this.mutateState(s => {
                                    s.requestingStaffCheckList.firstOrDefault(x => x.Id === id).Checked = checked;
                                    s.globalRequestingStaffList.firstOrDefault(x => x.Id === id).Checked = checked;
                                })}
                                sort
                                limitTextWidth
                                enableLazyRendering
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "createdByCliniciansSelect",
                node: (
                    <div>
                        <input
                            className="form-control medical-settings search-text"
                            value={this.state.createdBySearchText}
                            onChange={(e: any) => {
                                const value = e.target.value;
                                this.mutateState(s => s.createdBySearchText = value, () => this.updateEmployeeList());
                            }}
                            placeholder={cacheStore.GetString("SearchLabel").toUpperCase()}
                        />
                        <div className="medical-settings site-control">
                            <CheckedListControl
                                idModifier="createdByList"
                                DataSource={this.state.createdByCheckList.toImmutable()}
                                onChange={(checked, id) => this.mutateState(s => {
                                    s.createdByCheckList.firstOrDefault(x => x.Id === id).Checked = checked;
                                    s.globalCreatedByList.firstOrDefault(x => x.Id === id).Checked = checked;
                                })}
                                sort
                                limitTextWidth
                                enableLazyRendering
                            />
                        </div>
                    </div>
                )
            },
            {
                id: "prioritiesSelect",
                node: (
                    <div className="medical-settings priority-controls">
                        <CheckedListControl
                            DataSource={this.state.priorityCheckList.toImmutable()}
                            resolveValueInStringChange
                            onChange={(checked, id) => this.mutateState(s => s.priorityCheckList.firstOrDefault(x => x.Id === id).Checked = checked)}
                        />
                    </div>
                )
            },
            {
                id: "statusesSelect",
                node: (
                    <div className="medical-settings status-controls">
                        <CheckedListControl
                            sort={false}
                            DataSource={this.state.statusCheckList.toImmutable()}
                            onChange={(checked, id) => this.mutateState(s => s.statusCheckList.firstOrDefault(x => x.Id === id).Checked = checked)}
                        />
                    </div>
                )
            }
        ]
            .filter(p => !!p.node);
    }

    protected getSearchSettingsFooterProps(): SearchSettingsFooterProps {
        return {
            onClose: () => sessionStore.dispatch(this.props.onCloseOp),
            onOk: () => this.onOkClick(),
            onColumnOrderingButtonClick: () => sessionStore.dispatch(this.props.onColumnOrderingButtonClickOp),
            onResultsPerPageChange: value => sessionStore.dispatch(this.props.onResultsPerPageChangeOp, { value }),
            selectedPageSizeOption: this.props.model.get("SelectedPageSizeOption"),
            pageSizeOptions: this.props.model.get("PageSizeOptions").toArray(),
            isSaveSettingsChecked: this.state.isSaveSettingsChecked,
            onSaveSettingsCheckboxChange: checked => this.mutateState(s => s.isSaveSettingsChecked = checked)
        };
    }

    private getUpperCaseString(labelKey: string): string {
        return cacheStore.GetString(labelKey).toUpperCase();
    }

    private async onOkClick(): Promise<void> {
        const updatedModel = this.getUpdatedModel();
        if (this.state.isSaveSettingsChecked)
            await this.saveSettings(updatedModel);

        sessionStore.dispatch(this.props.onOkOp, { updatedModel });
    }

    private getUpdatedModel(): IConsultationRequestInboxModel | ILabRequestsInboxModel {
        const s = this.state;
        const header = s.headerCheckboxes;
        return this.props.model
            .set("SelectAllSitesChecked", header.sites)
            .set("SelectAllCreatedByChecked", header.createdBy)
            .set("SelectAllRequestingStaffChecked", header.requestingStaff)
            .set("SelectAllStatusChecked", header.status)
            .set("SelectAllPriorityChecked", header.priority)
            .set("CreationDateFrom", s.creationDateFrom)
            .set("CreationDateTo", s.creationDateTo)
            .set("DueDateFrom", s.dueDateFrom)
            .set("DueDateTo", s.dueDateTo)
            .set("Sites", s.siteCheckList.toImmutable())
            .set("CreatedByGlobalCache", s.globalCreatedByList.toImmutable())
            .set("RequestingStaffGlobalCache", s.globalRequestingStaffList.toImmutable())
            .set("RequestingStaffList", s.requestingStaffCheckList.toImmutable())
            .set("CreatedByClinicianList", s.createdByCheckList.toImmutable())
            .set("PriorityList", s.priorityCheckList.toImmutable())
            .set("StatusList", s.statusCheckList.toImmutable())
            .set("CreatedByCliniciansSearchText", s.createdBySearchText)
            .set("RequestingStaffsSearchText", s.requestingStaffSearchText);
    }

    private updateEmployeeList(): void {
        this.mutateState(s => {
            const siteIds = s.siteCheckList.filter(site => site.Checked).map(x => x.Id);
            const filteredCreatedByEmployees = s.globalCreatedByList.filter(employee => employee.Sites.some(employeeSiteId => siteIds.contains(employeeSiteId)));
            const filteredRequestingStaff = s.globalRequestingStaffList.filter(employee => employee.Sites.some(employeeSiteId => siteIds.contains(employeeSiteId)));
            s.createdByCheckList = s.createdBySearchText?.trim()
                ? SearchMenuHelper.FilterCliniciansByName(s.createdBySearchText.trim(), filteredCreatedByEmployees.toImmutable()).toArray()
                : filteredCreatedByEmployees;

            s.requestingStaffCheckList = s.requestingStaffSearchText?.trim()
                ? SearchMenuHelper.FilterCliniciansByName(s.requestingStaffSearchText?.trim(), filteredRequestingStaff.toImmutable()).toArray()
                : filteredRequestingStaff;
        });
    }

    private async saveSettings(updatedModel: IConsultationRequestInboxModel | ILabRequestsInboxModel): Promise<void> {
        const userSettings = new UserSettings();
        const medicalRequestSettings = MedicalRequestsInboxSettings.fromModel(updatedModel);

        if (this.props.type === MedicalRequestType.ConsultationRequest)
            userSettings.ConsultationRequestsSearchSettings = medicalRequestSettings;
        else
            userSettings.RequestsSearchSettings = medicalRequestSettings;

        const response = await Client.postAsync(
            ServerOperations.Server.Myle_UpdateUserSettings,
            {
                UserSettings: userSettings,
                UserId: sessionStore.userId
            }
        );

        if (!response.isSuccess)
            console.error(response.errors);
        else if (this.props.type === MedicalRequestType.ConsultationRequest)
            cacheStore.UserSettings.ConsultationRequestsSearchSettings = medicalRequestSettings;
        else
            cacheStore.UserSettings.RequestsSearchSettings = medicalRequestSettings;
    }
}

export { MedicalRequestSettingsContainer };
